import { useMemo, useState } from "react"
import { DateTime } from "luxon"
import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons"

import { RefetchIntervals } from "../../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    MeetingOrderFilterType,
    meetingOrderFilterButtonConfig,
} from "../../../../../shared/components/filters/orderTypeFilter/helpers"
import { StandardOrderTypeFilter } from "../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { EmptyContent, EmptyContentVariant } from "../../../../../shared/components/skeleton/EmptyContent"
import { TimeOrDatePicker } from "../../../../../shared/components/table/TimeOrDatePicker"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { useListPagination } from "../../../../../shared/hooks/useListPagination"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { TimePeriodMeetingService } from "../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { groupMeetingsByDay } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/meetingDayGroups"
import { MeetingsDayList } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingsDayList"
import { UpcomingOrdersPanel } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/UpcomingOrdersPanel"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { useCurrentServiceCenter } from "../../../store/serviceCenterHooks"
import { MeetingListPagination } from "../../home/components/meetigs/MeetingListPagination"

import { fetchServiceCenterUpcomingMeetings, ServiceCenterUpcomingMeetingsQuery } from "../upcomingMeetingsApi"
import { toUpcomingMeetingRow, UpcomingMeetingsResponse } from "../upcomingMeetingsTypes"

const useStyles = makeStyles()((theme) => ({
    toolbar: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "1rem",
        paddingBottom: "1.5rem",
    },
    datePicker: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.common.white,
        },
    },
}))

const selectUpcomingMeetings = (data: UpcomingMeetingsResponse) => (data?.orders ?? []).map(toUpcomingMeetingRow)

export function UpcomingMeetings() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { serviceCenterId } = useCurrentServiceCenter()

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{ startDate: DateTime; endDate: DateTime }>({ startDate, endDate })
    const [timePeriod, setTimePeriod] = useState<TimePeriodMeetingService>(TimePeriodMeetingService.TODAY)
    const [isDatePicker, setIsDatePicker] = useState(false)
    const [orderType, setOrderType] = useState<MeetingOrderFilterType>(MeetingOrderFilterType.ALL)

    const meetingsResult = useApiQuery(fetchServiceCenterUpcomingMeetings, {
        queryName: ServiceCenterUpcomingMeetingsQuery.UPCOMING_MEETINGS,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            period: isDatePicker ? undefined : timePeriod,
            startDate: isDatePicker ? (dateState.startDate.toISODate() ?? undefined) : undefined,
            endDate: isDatePicker ? (dateState.endDate.toISODate() ?? undefined) : undefined,
        },
        enabled: Boolean(serviceCenterId),
        select: selectUpcomingMeetings,
        refetchInterval: RefetchIntervals.short,
    })

    const meetings = useMemo(() => {
        const orders = meetingsResult.data ?? []
        if (orderType === MeetingOrderFilterType.ALL) return orders
        return orders.filter((order) => order.orderKind === orderType)
    }, [meetingsResult.data, orderType])

    const { pagedItems, pagination } = useListPagination(meetings)
    const groups = useMemo(
        () => groupMeetingsByDay(pagedItems, dateTimeFactory, l10n),
        [pagedItems, dateTimeFactory, l10n],
    )

    return (
        <>
            <Box className={classes.toolbar}>
                <StandardOrderTypeFilter
                    isMarginBottom={false}
                    orderType={orderType}
                    setOrderType={setOrderType}
                    config={meetingOrderFilterButtonConfig}
                />
                <Box className={classes.datePicker}>
                    <TimeOrDatePicker<TimePeriodMeetingService>
                        isDatePicker={isDatePicker}
                        dateState={dateState}
                        setDateState={setDateState}
                        timePeriod={timePeriod}
                        handleChangeTimePeriod={setTimePeriod}
                        handleChangeTimeOrDatePicker={() => setIsDatePicker((prev) => !prev)}
                    />
                </Box>
            </Box>

            <LocalizedStrict
                id="user-home-meetings-panel-actions"
                attrs={{ collapseAriaLabel: true, expandAriaLabel: true }}
            >
                <UpcomingOrdersPanel
                    icon={faCalendarDays}
                    iconBackgroundColor={tailwindPalette.blue[100]}
                    iconColor={tailwindPalette.blue[400]}
                    titleId="user-home-upcoming-meetings"
                    titleFallback="Meetings"
                    counter={pagination.totalItems}
                    collapseAriaLabel=""
                    expandAriaLabel=""
                    footer={pagination.totalItems > 0 ? <MeetingListPagination {...pagination} /> : undefined}
                >
                    {meetingsResult.isInitialLoading ? (
                        <LoadingSpinner />
                    ) : !groups.length ? (
                        <LocalizedStrict id="service-center-reports-upcoming-meetings-empty" attrs={{ title: true }}>
                            <EmptyContent variant={EmptyContentVariant.SINGLE_LEFT} title="No meetings in period" />
                        </LocalizedStrict>
                    ) : (
                        <MeetingsDayList groups={groups} meetings={meetings} />
                    )}
                </UpcomingOrdersPanel>
            </LocalizedStrict>
        </>
    )
}
